import { clientConfig } from '@/config/clientConfig';
import { ApiRemoveItemsDto, useApi } from '@/shared/api';
import {
  UpdateItemsGqlInput,
  useUpdateAccountSchemaVersionMutation,
  useUpdateManyCollectionsMutation,
} from '@/shared/generated/graphql';
import { EncryptionType, KeyPair } from '@/shared/lib/secure-json/core/crypto-core/types';
import { SecureJsonBase } from '@/shared/lib/secure-json/core/secure-json-base';
import { SecureJsonCollection } from '@/shared/lib/secure-json/core/secure-json-collection';
import { SecureJsonSerializer } from '@/shared/lib/secure-json/core/secure-json-serializer';
import { secureJsonEncryptItem } from '@/shared/lib/secure-json/utils';
import { CollectionsSjMap } from '@/shared/lib/sj-orm';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { CurrentMigrationCollectionsData } from '@/shared/lib/sj-orm/migrator';
import { useProfileStore } from '@/shared/store/profile.store';

type Collection = {
  name: CollectionName;
  collection: SecureJsonBase;
};

export const useUpdateMigratedCollections = () => {
  const isBeneficiary = useProfileStore((s) => s.isBeneficiary);

  const api = useApi();
  const [updateAccountSchemaVersionMutation] = useUpdateAccountSchemaVersionMutation();
  const [updateManyCollectionsMutation] = useUpdateManyCollectionsMutation();

  const getMigratedDataBackend = async ({
    migratedData,
    schemaVersion,
    encryptionType,
    keyPair,
    initialCollections,
  }: {
    schemaVersion: number;
    encryptionType: EncryptionType;
    keyPair: KeyPair;
    migratedData: CurrentMigrationCollectionsData;
    initialCollections: CollectionsSjMap;
  }) => {
    const { encryptedCollections, removedCollectionItems } = prepareBackendUpdateData({
      encryptionType,
      initialCollections,
      migratedData,
    });

    const { data, errors } = await updateManyCollectionsMutation({
      variables: { data: { collections: encryptedCollections } },
    });

    await Promise.all(
      removedCollectionItems
        .filter((collectionData) => collectionData.items.length > 0)
        .map((collectionData) => api.collectionsRemoveMany(collectionData)),
    );

    if (errors?.length || !data?.updateManyCollectionsGql.results) {
      throw new Error('Migrated collections don`t updated');
    }

    await updateAccountSchemaVersionMutation({
      variables: { input: { schemaVersion } },
    });

    const responseCollections = data.updateManyCollectionsGql.results;
    const decryptPromiseList = responseCollections.map(async ({ collectionName, updatedItems }) => {
      const serializer = new SecureJsonSerializer();
      const sjCollection = new SecureJsonBase(keyPair.privateKey, encryptionType);

      const items = new Map();

      updatedItems.forEach(({ dtoId, item }) => {
        items.set(dtoId, {
          encryptedData: item.encryptedData,
          encryptedSymmetricKeys: serializer.reviveValue(item.encryptedSymmetricKeys),
          permissions: serializer.reviveValue(item.permissions),
          signature: item.signature,
        });
      });

      const collection = await sjCollection.setItems({ items: items } as SecureJsonCollection);

      return { name: collectionName as CollectionName, collection };
    });

    const updatedCollections = await Promise.all(decryptPromiseList);

    return updatedCollections;
  };

  const updateMigratedCollections = async ({
    encryptionType,
    keyPair,
    schemaVersion,
    migratedData,
    initialCollections,
  }: {
    encryptionType: EncryptionType;
    keyPair: KeyPair;
    schemaVersion: number;
    migratedData: CurrentMigrationCollectionsData;
    initialCollections: CollectionsSjMap;
  }) => {
    let migratedCollections: Collection[];

    if (isBeneficiary || clientConfig.isExported) {
      migratedCollections = getMigratedDataLocal({
        encryptionType,
        keyPair,
        migratedData,
        initialCollections,
      });
    } else {
      migratedCollections = await getMigratedDataBackend({
        schemaVersion,
        encryptionType,
        keyPair,
        migratedData,
        initialCollections,
      });
    }

    return migratedCollections;
  };

  return { updateMigratedCollections };
};

const getMigratedDataLocal = ({
  encryptionType,
  keyPair,
  migratedData,
  initialCollections,
}: {
  encryptionType: EncryptionType;
  keyPair: KeyPair;
  migratedData: CurrentMigrationCollectionsData;
  initialCollections: CollectionsSjMap;
}) => {
  const updatedCollections = Array.from(migratedData.entries())
    .filter(([name]) => initialCollections.get(name) !== undefined)
    .map(([name, migratedCollection]) => {
      const collection = initialCollections.get(name);
      const defaultCollection = new SecureJsonBase(keyPair.privateKey, encryptionType);

      const items = new Map();

      migratedCollection.forEach((item, dtoId) => {
        // initialItem checked in filter
        const initialItem = collection!.get(dtoId);

        items.set(dtoId, {
          decryptedData: item,
          encryptedSymmetricKeys: initialItem.encryptedSymmetricKeys,
          permissions: initialItem.permissions,
          signature: initialItem.signature,
        });
      });

      defaultCollection.items = items;

      return { name, collection: defaultCollection };
    });

  return updatedCollections;
};

type DataOptions = {
  migratedData: CurrentMigrationCollectionsData;
  initialCollections: CollectionsSjMap;
  encryptionType: EncryptionType;
};

type EncryptedCollection = {
  items: UpdateItemsGqlInput['items'];
  collectionName: CollectionName;
};

type RemovedCollectionItems = {
  collectionName: CollectionName;
  items: ApiRemoveItemsDto[];
};

const prepareBackendUpdateData = ({
  migratedData,
  initialCollections,
  encryptionType,
}: DataOptions) => {
  const encryptedCollections: EncryptedCollection[] = [];
  const removedCollectionItems: RemovedCollectionItems[] = [];

  migratedData.forEach((migratedCollection, collectionName) => {
    const collection = initialCollections.get(collectionName);

    if (!collection) {
      return;
    }

    const items: UpdateItemsGqlInput['items'] = [];
    const removedItems: ApiRemoveItemsDto[] = [];

    collection?.items.forEach((value, key) => {
      const migratedItem = migratedCollection.get(key);

      if (!migratedItem && value.signature) {
        removedItems.push({ dtoId: key, signature: value.signature });
        return;
      }

      const rawData = {
        dtoId: key,
        item: secureJsonEncryptItem({ ...value, decryptedData: migratedItem }, encryptionType),
        platform: clientConfig.api.collectionPlatformKey,
        signature: value.signature,
      };

      items.push(rawData);
    });

    encryptedCollections.push({ items: items, collectionName });
    removedCollectionItems.push({ items: removedItems, collectionName });
  });

  return {
    encryptedCollections,
    removedCollectionItems,
  };
};
